import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { presignUpload, uploadFileToS3 } from "./bannerApi";

import FormModal from "../../../components/form/FormModal";
import FormButton from "../../../components/form/FormButton";
import FormCheckbox from "../../../components/form/FormCheckbox";
import FormField from "../../../components/form/FormField";
import FormInput from "../../../components/form/FormInput";
import FormSelect from "../../../components/form/FormSelect";

import "./banner.css";

const COUNTRY_OPTIONS = [
  { value: "IN", label: "India" },
  { value: "NP", label: "Nepal" },
  { value: "SA", label: "Saudi Arabia" },
  { value: "AE", label: "UAE" },
];

const emptyForm = {
  title: "",
  imageUrl: "",
  imageKey: "",
  redirectType: "listing",
  redirectValue: "",
  priority: 1,
  isGlobal: true,
  country: "",
  active: true,
};

const MAX_SIZE = 5 * 1024 * 1024;

const ALLOWED_TYPES = [
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/webp",
  "image/gif",
  "image/avif",
  "image/svg+xml",
];

const fieldLabel = (type) => {
  switch (type) {
    case "venue":
      return "Venue ID";
    case "external":
      return "External URL";
    default:
      return "Listing Slug / ID";
  }
};

const apiToUiRedirectType = (value) => {
  const key = String(value || "").toLowerCase();

  if (key === "venue") return "venue";
  if (key === "external") return "external";

  return "listing";
};

const uiToApiRedirectType = (value) => {
  const key = String(value || "").toLowerCase();

  if (key === "venue") return "venue";
  if (key === "external") return "external";

  return "listing";
};

const showFirstValidationToast = (errors) => {
  const firstError = Object.values(errors).find(Boolean);
  if (firstError) toast.error(firstError);
};

const BannerForm = ({
  open,
  mode = "create",
  initialData = null,
  onCancel,
  onSubmit,
}) => {
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const ASSET_BASE = (
    process.env.REACT_APP_CDN_URL ||
    process.env.REACT_APP_ASSET_BASE_URL ||
    ""
  ).replace(/\/+$/, "");

  useEffect(() => {
    setErrors({});
    setSelectedFile(null);
    setProgress(0);

    if (!initialData) {
      setForm(emptyForm);
      return;
    }

    setForm({
      title: initialData.title || "",
      imageUrl: initialData.imageUrl || "",
      imageKey: initialData.imageKey || "",
      redirectType: apiToUiRedirectType(initialData.redirectType),
      redirectValue: initialData.redirectValue || "",
      priority: initialData.priority ?? 1,
      isGlobal:
        typeof initialData.isGlobal === "boolean" ? initialData.isGlobal : true,
      country: initialData.country || "",
      active: typeof initialData.active === "boolean" ? initialData.active : true,
    });
  }, [initialData]);

  const previewSrc = useMemo(() => {
    if (form.imageKey && ASSET_BASE) return `${ASSET_BASE}/${form.imageKey}`;
    return String(form.imageUrl || "").trim();
  }, [form.imageKey, form.imageUrl, ASSET_BASE]);

  const set = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const clearError = (key) => {
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleFilePick = (file) => {
    if (!file) return;

    if (!ALLOWED_TYPES.includes(file.type)) {
      const message = "Unsupported file type";
      setErrors((prev) => ({ ...prev, imageUrl: message }));
      toast.error(message);
      return;
    }

    if (file.size > MAX_SIZE) {
      const message = "File is too large (max 5MB)";
      setErrors((prev) => ({ ...prev, imageUrl: message }));
      toast.error(message);
      return;
    }

    setSelectedFile(file);
    clearError("imageUrl");
  };

  const doUpload = async () => {
    if (!selectedFile || uploading) return;

    setUploading(true);
    setProgress(0);

    try {
      const ext =
        selectedFile.type === "image/png"
          ? "png"
          : selectedFile.type === "image/webp"
          ? "webp"
          : selectedFile.type === "image/gif"
          ? "gif"
          : selectedFile.type === "image/svg+xml"
          ? "svg"
          : selectedFile.type === "image/avif"
          ? "avif"
          : "jpg";

      const scope = form.isGlobal ? "global" : String(form.country || "unknown").toLowerCase();
      const folder = `banners/${scope}`;
      const filename = `banner_${Date.now()}.${ext}`;

      const presign = await presignUpload({
        folder,
        filename,
        contentType: selectedFile.type,
      });

      await uploadFileToS3({
        presign,
        file: selectedFile,
        onProgress: (nextProgress) => setProgress(nextProgress),
      });

      setForm((prev) => ({
        ...prev,
        imageKey: presign.filePath,
        imageUrl: presign.publicUrl || "",
      }));

      setSelectedFile(null);
      setProgress(100);
      clearError("imageUrl");

      toast.success("Image uploaded");
    } catch (error) {
      console.error(error);
      const message = "Upload failed. Try again.";
      setErrors((prev) => ({ ...prev, imageUrl: message }));
      toast.error(message);
    } finally {
      setUploading(false);
      setTimeout(() => setProgress(0), 800);
    }
  };

  const validate = () => {
    const nextErrors = {};

    if (!String(form.title || "").trim()) {
      nextErrors.title = "Title is required";
    }

    if (!form.redirectType) {
      nextErrors.redirectType = "Choose a type";
    }

    if (!String(form.redirectValue || "").trim()) {
      nextErrors.redirectValue = "This field is required";
    }

    const priority = parseInt(form.priority, 10);
    if (Number.isNaN(priority) || priority < 0) {
      nextErrors.priority = "Priority must be a non-negative number";
    }

    const isGlobal = form.isGlobal ?? true;
    const country = String(form.country || "").trim();

    if (!isGlobal && !country) {
      nextErrors.country = "Country is required for non-global banners";
    }

    if (mode === "create" && !form.imageKey) {
      nextErrors.imageUrl = "Please upload an image";
    }

    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      showFirstValidationToast(nextErrors);
    }

    return nextErrors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (uploading || submitting) return;

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) return;

    const isGlobal = form.isGlobal ?? true;
    const country = String(form.country || "").trim();
    const active = typeof form.active === "boolean" ? form.active : true;

    const payload = {
      title: form.title.trim(),
      imageKey: form.imageKey || undefined,
      redirectType: uiToApiRedirectType(form.redirectType),
      redirectValue: form.redirectValue.trim(),
      priority: parseInt(form.priority, 10),
      isGlobal: !!isGlobal,
      active,
      ...(isGlobal ? {} : { country: country || undefined }),
    };

    try {
      setSubmitting(true);
      await onSubmit?.(payload);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (uploading || submitting) return;
    onCancel?.();
  };

  return (
    <FormModal
      open={open}
      title={mode === "create" ? "Create Banner" : "Edit Banner"}
      width="800px"
      submitting={uploading || submitting}
      submitText={mode === "create" ? "Create Banner" : "Save Changes"}
      submittingText={uploading ? `Uploading ${progress || 0}%` : "Saving..."}
      cancelText="Cancel"
      onClose={handleClose}
      onSubmit={handleSubmit}
    >
      <div className="banner-form">
        <div className="banner-grid">
          <FormField label="Title" error={errors.title}>
            <FormInput
              type="text"
              value={form.title}
              onChange={(event) => {
                set("title", event.target.value);
                clearError("title");
              }}
              placeholder="Summer Promo"
              required
            />
          </FormField>

          <FormField
            label="Scope"
            hint="Global banners appear in all countries; country-specific banners only in that country."
          >
            <FormSelect
              value={form.isGlobal ? "global" : "country"}
              onChange={(event) => {
                const isGlobal = event.target.value === "global";
                set("isGlobal", isGlobal);
                if (isGlobal) set("country", "");
                clearError("country");
              }}
            >
              <option value="global">Global</option>
              <option value="country">Country specific</option>
            </FormSelect>
          </FormField>

          {!form.isGlobal && (
            <FormField label="Country" error={errors.country}>
              <FormSelect
                value={form.country || ""}
                onChange={(event) => {
                  set("country", event.target.value);
                  clearError("country");
                }}
              >
                <option value="">Select country</option>
                {COUNTRY_OPTIONS.map((country) => (
                  <option key={country.value} value={country.value}>
                    {country.label}
                  </option>
                ))}
              </FormSelect>
            </FormField>
          )}

          <FormField
            label="Priority"
            error={errors.priority}
            hint="Higher priority shows first."
          >
            <FormInput
              type="number"
              min={0}
              value={form.priority}
              onChange={(event) => {
                set("priority", event.target.value);
                clearError("priority");
              }}
              placeholder="1"
              required
            />
          </FormField>

          <FormField label="Redirect Type" error={errors.redirectType}>
            <FormSelect
              value={form.redirectType}
              onChange={(event) => {
                set("redirectType", event.target.value);
                clearError("redirectType");
              }}
            >
              <option value="listing">Listing</option>
              <option value="venue">Venue</option>
              <option value="external">External</option>
            </FormSelect>
          </FormField>

          <FormField label={fieldLabel(form.redirectType)} error={errors.redirectValue}>
            <FormInput
              type="text"
              value={form.redirectValue}
              onChange={(event) => {
                set("redirectValue", event.target.value);
                clearError("redirectValue");
              }}
              placeholder={fieldLabel(form.redirectType)}
              required
            />
          </FormField>

          <FormField
            label="Banner Image"
            error={errors.imageUrl}
            hint="Upload an image from your computer; it will be stored on AWS S3."
            className="banner-span-2"
          >
            <div className="banner-upload-row">
              <FormInput
                type="file"
                accept="image/*"
                onChange={(event) => handleFilePick(event.target.files?.[0])}
                disabled={uploading || submitting}
              />

              <FormButton
                type="button"
                variant="primary"
                onClick={doUpload}
                disabled={!selectedFile || uploading || submitting}
                className="banner-upload-btn"
              >
                {uploading ? `Uploading ${progress || 0}%` : "Upload"}
              </FormButton>
            </div>

            {selectedFile && !uploading && (
              <small className="banner-selected-file">Selected: {selectedFile.name}</small>
            )}

            <FormInput
              type="text"
              value={form.imageUrl}
              placeholder="S3 URL will appear here after upload"
              readOnly
              className="banner-image-url"
            />

            <input type="hidden" value={form.imageKey || ""} readOnly />
          </FormField>

          <FormField label="Preview" className="banner-span-2">
            <div className="banner-image-preview">
              {previewSrc ? (
                <img src={previewSrc} alt="Banner preview" />
              ) : (
                <div className="banner-image-placeholder">No image yet</div>
              )}
            </div>
          </FormField>

          <FormField label="Status" className="banner-span-2">
            <FormCheckbox
              checked={!!form.active}
              onChange={(event) => set("active", event.target.checked)}
              label="Active (visible to users)"
            />
          </FormField>
        </div>
      </div>
    </FormModal>
  );
};

export default BannerForm;